import { baseApi as api } from "../baseApi";
const injectedRtkApi = api.injectEndpoints({
  endpoints: (build) => ({
    getV2ProviderJotcoachEncounterSessions: build.query<
      GetV2ProviderJotcoachEncounterSessionsApiResponse,
      GetV2ProviderJotcoachEncounterSessionsApiArg
    >({
      query: (queryArg) => ({
        url: `/v2/provider/jotcoach/encounter-sessions`,
        params: {
          page: queryArg.page,
          per_page: queryArg.perPage,
        },
      }),
    }),
    getV2ProviderJotcoachEncounterSessionsByEncounterId: build.query<
      GetV2ProviderJotcoachEncounterSessionsByEncounterIdApiResponse,
      GetV2ProviderJotcoachEncounterSessionsByEncounterIdApiArg
    >({
      query: (queryArg) => ({
        url: `/v2/provider/jotcoach/encounter-sessions/${queryArg.encounterId}`,
      }),
    }),
    getV2ProviderJotcoachRules: build.query<
      GetV2ProviderJotcoachRulesApiResponse,
      GetV2ProviderJotcoachRulesApiArg
    >({
      query: () => ({ url: `/v2/provider/jotcoach/rules` }),
    }),
  }),
  overrideExisting: false,
});
export { injectedRtkApi as enhancedApi };
export type GetV2ProviderJotcoachEncounterSessionsApiResponse =
  /** status 200 OK */ ListResponseEncounterSessionListItemResponse;
export type GetV2ProviderJotcoachEncounterSessionsApiArg = {
  /** Page number (1-based) */
  page?: number;
  /** Items per page (max 1000) */
  perPage?: number;
};
export type GetV2ProviderJotcoachEncounterSessionsByEncounterIdApiResponse =
  /** status 200 OK */ EncounterSessionResponse;
export type GetV2ProviderJotcoachEncounterSessionsByEncounterIdApiArg = {
  encounterId: string;
};
export type GetV2ProviderJotcoachRulesApiResponse =
  /** status 200 OK */ JotCoachRulesResponse;
export type GetV2ProviderJotcoachRulesApiArg = void;
export type EncounterSessionListItemResponse = {
  /** Session creation timestamp */
  created_at: string;
  /** Document ID (jotdoc or note UUID) */
  document_id: string;
  /** Document type: 'jotdoc' or 'note' */
  document_type: string;
  /** Encounter ID */
  encounter_id: string;
  /** Parent encounter session identifier (UUID) */
  jotcoach_encounter_session_id: string;
  /** Note ID associated with the encounter */
  note_id?: string | null;
  /** Organization ID for the session */
  organization_id: string;
  /** Overall quality score (1-5) from rules analysis */
  overall_quality_score?: number | null;
  /** Patient ID associated with the encounter */
  patient_id?: string | null;
  /** Patient display name (preferred_name or first_name with last_name) */
  patient_name?: string | null;
  /** Provider name (signing_name or name) */
  provider_name?: string | null;
  /** Selected CPT code from session analysis (primary code) */
  selected_cpt_code?: string | null;
  /** Last update timestamp */
  updated_at: string;
  /** User ID (provider) */
  user_id: string;
};
export type ListResponseEncounterSessionListItemResponse = {
  items: EncounterSessionListItemResponse[];
  page?: number;
  per_page?: number;
  total?: number;
  total_pages?: number;
};
export type ClientErrorResponse = {
  /** The error code for the error */
  error_code?: string;
  /** The message for the error */
  message?: string;
  /** The HTTP status code for the client error */
  status?: number;
};
export type ValidationErrorElement = {
  ctx?: object;
  loc: string[];
  msg: string;
  type: string;
};
export type ValidationError = ValidationErrorElement[];
export type EncounterSessionResponse = {
  /** Session creation timestamp */
  created_at: string;
  /** Encounter ID */
  encounter_id: string;
  /** Session identifier (UUID) */
  jotcoach_encounter_session_id: string;
  /** Organization ID for the session */
  organization_id: string;
  /** Dictionary of workflow details keyed by prefixed document IDs (e.g., 'jotdoc:uuid' or 'note:uuid') */
  sessions?: {
    [key: string]: {
      [key: string]: any;
    };
  };
  /** Last update timestamp */
  updated_at: string;
  /** User ID (provider) */
  user_id: string;
};
export type RuleFacets = {
  /** Applicable CPT codes */
  cpt?: string[];
  /** Applicable payers (e.g., Optum, Medicare) */
  payer?: string[];
  /** Applicable provider types (e.g., PMHNP) */
  provider_type?: string[];
};
export type RuleOwnerScope = "public" | "org" | "user";
export type RuleSource = {
  /** Date of source document (YYYY-MM-DD or string) */
  document_date?: string | null;
  /** Title of source document */
  document_title?: string | null;
  /** URI/URL to source document */
  source_uri?: string | null;
};
export type RuleState = "active" | "draft" | "deprecated";
export type ComplianceRule = {
  /** When true, rule bypasses LLM selection and is always included */
  always_select?: boolean;
  /** Canonical Markdown content: Rule, Applies when, What good looks like, Why it matters */
  body_md: string;
  /** Sparse applicability filters: cpt, provider_type, payer */
  facets?: RuleFacets;
  /** True if the rule has been soft-deleted (for historical data) */
  is_deleted?: boolean;
  /** True if the rule is disabled for the organization (not active) */
  is_disabled?: boolean;
  /** Owning organization UUID when not public */
  organization_id?: string | null;
  /** Scope of the rule: public, org, or user */
  owner_scope?: RuleOwnerScope;
  /** Reference to parent rule when forked, for lineage tracking */
  parent_rule_id?: string | null;
  /** Unique stable identifier for the rule */
  rule_id: string;
  /** Compact one-liner for LLM rule selection (~100-150 chars) */
  selector_text: string;
  /** Source documentation: document_title, document_date, source_uri */
  source?: RuleSource;
  /** Rule lifecycle state: active or deprecated */
  state?: RuleState;
  /** Short human-readable label */
  title?: string | null;
  /** Last update timestamp (UTC) */
  updated_at?: string | null;
};
export type JotCoachRulesResponse = {
  /** List of compliance rules for JotCoach */
  rules: ComplianceRule[];
  /** Total number of rules */
  total: number;
};
export type ServerErrorResponse = {
  /** The error code for the error */
  error_code?: string;
  /** The message for the error */
  message?: string;
  /** The HTTP status code for the server error */
  status?: number;
};
